import { HttpErrorResponse, HttpResponse } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { SelectItem } from 'primeng/api'
import { EMPTY, Observable, catchError, forkJoin, map } from 'rxjs'
import { StorageKeys } from 'src/app/domain/enums/application/storage-keys.enum'
import { ToastSeverity } from 'src/app/domain/enums/components/toast.enum'
import { BorealUploadType } from 'src/app/domain/enums/components/upload.enum'
import { ApiResponse } from 'src/app/domain/interfaces/application/api-response.interface'
import { Toast } from 'src/app/domain/interfaces/components/toast.interface'
import { AuthUserCompany } from 'src/app/domain/models/application/auth-user-company.model'
import { BlobManagement } from 'src/app/domain/utils/classes/blob-management'
import { formatStringForAwsFileBucketFormat } from 'src/app/domain/utils/functions/format-string'
import { CompanyRepositoryService } from '../../../repositories/application/company/company-repository.service'
import { ToastService } from '../../components/toast/toast.service'
import { FileService } from '../../erp/file/file.service'
import { RegistrationBankAccountService } from '../../erp/registration/financial/registration-bank-account/registration-bank-account.service'
@Injectable({ providedIn: 'root' })
export class CompanyService {
  private companyRepositoryService: CompanyRepositoryService = inject(CompanyRepositoryService)
  private toastService: ToastService = inject(ToastService)
  private fileService: FileService = inject(FileService)
  private registrationBankAccountService: RegistrationBankAccountService = inject(RegistrationBankAccountService)

  blobManagement: BlobManagement = new BlobManagement()

  public setSelectedCompany(company: AuthUserCompany): void {
    this.companyRepositoryService.setSelectedCompany(company)
  }

  public setSelectedCompanyBranches(companyBranches: Array<number>): void {
    this.companyRepositoryService.setSelectedCompanyBranches(companyBranches)
  }

  public getSelectedCompany(): AuthUserCompany {
    return this.companyRepositoryService.getSelectedCompany()
  }

  public getSelectedCompanyBranches(): Array<number> {
    return this.companyRepositoryService.getSelectedCompanyBranches()
  }

  public getSelectedCompanyId(): number {
    return this.companyRepositoryService.getSelectedCompany().value
  }

  public getSelectedParentCompanyId(): number {
    return this.companyRepositoryService.getSelectedCompany().parentId ?? 0
  }

  public getSelectedCompanyCnpj(): string {
    return String(this.companyRepositoryService.getSelectedCompany().cnpj)
  }

  public getSelectedCompanyFantasy(): string {
    return this.companyRepositoryService.getSelectedCompany().label
  }

  public getTaskCompanyParameterActive(): boolean {
    return this.companyRepositoryService.getSelectedCompany().taskParameterActive
  }

  public setTaskCompanyParameterActive(active: boolean): void {
    const selectedCompany = this.companyRepositoryService.getSelectedCompany()
    selectedCompany.taskParameterActive = active
    this.companyRepositoryService.setSelectedCompany(selectedCompany)
  }

  public hasSelectedCompany(): boolean {
    return !!this.companyRepositoryService.getSelectedCompany()?.value
  }

  public ensureSelectedCompany(companies: Array<AuthUserCompany>): AuthUserCompany | null {
    if (!companies.length) return null

    const companyById = (companyId: number) => companies.find((company: AuthUserCompany) => company.value === companyId)
    const previousCompanyId = this.getSelectedCompanyId()
    const lastCompanyId = Number(localStorage.getItem(StorageKeys.KEY_LAST_COMPANY) ?? 0)
    const companyToSelect = companyById(previousCompanyId) ?? companyById(lastCompanyId) ?? companies[0]

    this.setSelectedCompany(companyToSelect)

    if (companyToSelect.value !== previousCompanyId) {
      this.setSelectedCompanyBranches((companyToSelect.branches ?? []).map((branch: any) => Number(branch.value)))
      localStorage.setItem(StorageKeys.KEY_LAST_COMPANY, companyToSelect.value.toString())
    }

    return companyToSelect
  }

  public getOptionsAllUsers(companyId: number, fullName: boolean = false): Observable<Array<SelectItem>> {
    return this.companyRepositoryService.getAllUsers(companyId).pipe(
      map((res: HttpResponse<any>) => {
        let allUserOptions = new Array()

        res.body?.users.forEach((user: any) => {
          allUserOptions.push({ label: fullName ? user.nome_completo : user.alias, value: user.id })
        })

        return allUserOptions
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: 'Falha ao recuperar os usuários desta empresa!' } as Toast)
        return new Array()
      })
    )
  }

  public getIntegrations(): Observable<any> {
    return this.companyRepositoryService.getIntegrations().pipe(
      map((res: HttpResponse<any>) => {
        return res.body.data
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: 'Falha ao recuperar as integrações de API desta empresa!' } as Toast)
        return new Array()
      })
    )
  }

  public getFinancialParameter(companyId: number): Observable<any> {
    return this.companyRepositoryService.getFinancialParameter(companyId).pipe(
      map((res: HttpResponse<any>) => {
        return res.body.value[0]
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: 'Falha ao recuperar os parâmetros financeiros da empresa!' } as Toast)
        return EMPTY
      })
    )
  }

  public getAccountingParameter(companyId: number): Observable<any> {
    return this.companyRepositoryService.getAccountingParameter(companyId).pipe(
      map((res: HttpResponse<any>) => {
        return res.body.value[0]
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: 'Falha ao recuperar os parâmetros contabeis da empresa!' } as Toast)
        return EMPTY
      })
    )
  }

  public getParameter(companyId: number): Observable<any> {
    return this.companyRepositoryService.getParameter(companyId).pipe(
      map((res: HttpResponse<any>) => {
        return res.body.value[0]
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: 'Falha ao recuperar os parâmetros da empresa!' } as Toast)
        return EMPTY
      })
    )
  }

  public getOptionsCashFlowModel(companyId: number): Observable<Array<SelectItem>> {
    return this.companyRepositoryService.getCashFlowModel(companyId).pipe(
      map((res: HttpResponse<any>) => {
        let optionsCashFlowModel: Array<SelectItem> = new Array()

        res.body.data.forEach((option: any) => {
          const createdOption: SelectItem = { label: option.nome_fluxo, value: option.id }

          optionsCashFlowModel.push(createdOption)
        })

        return optionsCashFlowModel
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: 'Falha ao recuperar os modelos de fluxo de caixa da empresa!' } as Toast)
        return EMPTY
      })
    )
  }

  public getOptionsGroup(companyId: number, userId: number): Observable<Array<SelectItem>> {
    return this.companyRepositoryService.getGroup(companyId, userId).pipe(
      map((res: HttpResponse<any>) => {
        let optionsGroup: Array<any> = new Array()

        res.body?.data?.forEach((company: any) => optionsGroup.push({ label: company.fantasia, value: company.id, document: company.cnpj }))

        return optionsGroup
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: 'Falha ao recuperar as empresas do grupo!' } as Toast)
        return EMPTY
      })
    )
  }

  public getDefaultBankAccountTypes(): Observable<Array<number>> {
    return this.companyRepositoryService.getCompanyFinancialParameters().pipe(
      map((res: HttpResponse<any>) => (res.body?.data?.standardAccountTypeIds ?? []) as Array<number>),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: 'Falha ao recuperar tipos de conta padrão!' } as Toast)
        return EMPTY
      })
    )
  }

  public getDefaultBankAccounts(_companyId: number): Observable<Array<any>> {
    return forkJoin({
      financialParameters: this.companyRepositoryService.getCompanyFinancialParameters(),
      companyAccounts: this.registrationBankAccountService.getCompanyAccountOptions(false)
    }).pipe(
      map(({ financialParameters, companyAccounts }) => {
        const defaultAccountIds = new Set(this.buildDefaultAccountsIds(companyAccounts, financialParameters.body?.data))

        return companyAccounts
          .filter((account: any) => defaultAccountIds.has(account.value))
          .map((account: any) => ({ id: account.value, nome: account.label }))
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: 'Falha ao recuperar tipos de conta padrão!' } as Toast)
        return EMPTY
      })
    )
  }

  public getMandatoryApportionment(parentCompanyId: number): Observable<Array<SelectItem>> {
    return this.companyRepositoryService.getMandatoryApportionment(parentCompanyId).pipe(
      map((res: HttpResponse<any>) => {
        return res.body
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: 'Falha ao recuperar o centro de custo obrigatório!' } as Toast)
        return EMPTY
      })
    )
  }

  public getCertificateStatus(companyId: number): Observable<any> {
    return this.companyRepositoryService.getCertificateStatus(companyId).pipe(
      map((res: HttpResponse<any>) => {
        return res.body.data
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        return EMPTY
      })
    )
  }

  public getCompanyGeneralParameters(companyId: number): Observable<any> {
    return this.companyRepositoryService.getCompanyGeneralParameters(companyId).pipe(
      map((res: HttpResponse<any>) => {
        return res.body.data
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: 'Falha ao recuperar os parâmetros da empresa!' } as Toast)
        return EMPTY
      })
    )
  }

  public save(company: AuthUserCompany, deploymentType: number, modulesToClone: Array<string>, baseCompanyId: number): Observable<any> {
    const payload: any = { ...company, stockGroup: false, clone: deploymentType === 2, modulesToClone: modulesToClone, baseCompanyId: baseCompanyId }

    return this.companyRepositoryService.save(payload).pipe(
      map((res: HttpResponse<any>) => {
        return res.body
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: httpError.error?.message ? httpError.error.message : 'Falha ao salvar a empresa!' } as Toast)
        return EMPTY
      })
    )
  }

  public getCommonInformation(): Observable<any> {
    return this.companyRepositoryService.getCommonInformation().pipe(
      map((res: HttpResponse<any>) => {
        return res.body.data
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: 'Falha ao recuperar as informações comuns da empresa!' } as Toast)
        return EMPTY
      })
    )
  }

  public uploadReportsLogo(file: File, company_id: number): Observable<any> {
    const payload: FormData = new FormData()

    payload.append('file', file)
    payload.append('file_name', formatStringForAwsFileBucketFormat(file.name))
    payload.append('upload_type', BorealUploadType.REPORTS_LOGO)
    payload.append('upload_type', BorealUploadType.REPORTS_LOGO)
    payload.append('company_id', company_id.toString())

    return this.fileService.uploadFileS3Boreal(payload, formatStringForAwsFileBucketFormat(file.name))
  }

  public uploadNfsDanfeLogo(file: File, company_id: number): Observable<any> {
    const payload: FormData = new FormData()

    payload.append('file', file)
    payload.append('file_name', formatStringForAwsFileBucketFormat(file.name))
    payload.append('upload_type', BorealUploadType.NFS_LOGO)
    payload.append('company_id', company_id.toString())

    return this.fileService.uploadFileS3Boreal(payload, formatStringForAwsFileBucketFormat(file.name))
  }

  public uploadCertificateFile(file: File, company_id: number, password: string): Observable<any> {
    const payload: FormData = new FormData()

    payload.append('file', file)
    payload.append('file_name', formatStringForAwsFileBucketFormat(file.name))
    payload.append('upload_type', BorealUploadType.CERTIFICATE)
    payload.append('company_id', company_id.toString())
    payload.append('password', password)

    return this.fileService.uploadFileS3Boreal(payload, formatStringForAwsFileBucketFormat(file.name))
  }

  public exportExcel(): Observable<Blob> {
    return this.companyRepositoryService.exportExcel().pipe(
      map((res: HttpResponse<any>) => {
        this.blobManagement.blobDownload(res.body as Blob, 'relatorio-empresas')
        return res.body
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.blobManagement.blobError(httpError)
        return EMPTY
      })
    )
  }

  public getIdentityTypes(): Observable<any> {
    return this.companyRepositoryService.getIdentityTypes().pipe(
      map((res: HttpResponse<ApiResponse>) => {
        return res.body?.data
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.blobManagement.blobError(httpError)
        return EMPTY
      })
    )
  }

  private buildDefaultAccountsIds(companyAccounts: Array<any>, financialParametersData: any): Array<number> {
    const standardAccountTypeIds = new Set((financialParametersData?.standardAccountTypeIds ?? []) as Array<number>)

    return companyAccounts
      .filter((account) => {
        const accountTypeId = account.accountType?.id ?? account.accountTypeId
        return accountTypeId != null && standardAccountTypeIds.has(accountTypeId)
      })
      .map((account) => account.value)
  }
}
